import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Download as DownloadIcon, Trash2 } from 'lucide-react';
import { API, inputCls } from '@/lib/api';
import { authHeaders, apiError } from '@/admin/AdminContentContext';

const TYPES = { contact: 'اتصال', support: 'تذكرة دعم', maintenance: 'صيانة' };
const STATUSES = { pending: 'قيد الانتظار', in_progress: 'قيد المعالجة', resolved: 'تم الحل' };
const TYPE_COLORS = { contact: 'bg-brand-blue/10 text-brand-blue', support: 'bg-brand-amber/10 text-amber-700', maintenance: 'bg-brand-emerald/10 text-emerald-700' };
const STATUS_COLORS = { pending: 'bg-brand-amber/15 text-amber-700', in_progress: 'bg-brand-sky/15 text-sky-700', resolved: 'bg-brand-emerald/15 text-emerald-700' };

export default function Submissions() {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/submissions`, authHeaders()).then(({ data }) => setSubs(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const setSt = async (id, st) => {
    try { await axios.patch(`${API}/admin/submissions/${id}`, { status: st }, authHeaders()); setSubs((xs) => xs.map((x) => (x.id === id ? { ...x, status: st } : x))); toast.success('تم تحديث الحالة'); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm('حذف هذه الرسالة نهائياً؟')) return;
    try { await axios.delete(`${API}/admin/submissions/${id}`, authHeaders()); setSubs((xs) => xs.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => subs.filter((s) =>
    (filter === 'all' || s.type === filter) && (status === 'all' || s.status === status) &&
    (!q || JSON.stringify(s).toLowerCase().includes(q.toLowerCase()))), [subs, filter, status, q]);

  const counts = useMemo(() => subs.reduce((a, s) => { a[s.status] = (a[s.status] || 0) + 1; return a; }, {}), [subs]);

  const exportCsv = () => {
    const rows = [['id', 'type', 'name', 'phone', 'email', 'city', 'message', 'status', 'created_at']];
    filtered.forEach((s) => rows.push([s.id, s.type, s.name, s.phone, s.email, s.city || '', (s.message || '').replace(/[\n,]/g, ' '), s.status, s.created_at]));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + rows.map((r) => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    a.download = 'wiyakgps-submissions.csv';
    a.click();
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h2 className="font-display text-lg font-extrabold text-brand-ink">الرسائل والطلبات</h2>
          <p className="text-xs text-brand-steel">رسائل الاتصال، طلبات الصيانة، وتذاكر الدعم الفني.</p></div>
        <div className="flex gap-2 font-mono text-[10px] font-bold">
          {Object.entries(STATUSES).map(([k, v]) => <span key={k} className={`rounded-full px-3 py-1.5 ${STATUS_COLORS[k]}`} data-testid={`count-${k}`}>{v}: {counts[k] || 0}</span>)}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {['all', 'contact', 'support', 'maintenance'].map((f) => (
          <button key={f} data-testid={`admin-type-tab-${f}`} onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${filter === f ? 'border-brand-blue bg-brand-blue text-white' : 'border-brand-line bg-white text-brand-steel hover:text-brand-blue'}`}>
            {f === 'all' ? 'الكل' : TYPES[f]}
          </button>
        ))}
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-filter" className={`${inputCls} !w-40 !py-2 !text-xs`}>
          <option value="all">كل الحالات</option>
          {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="relative ms-auto">
          <Search className="absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" placeholder="بحث بالاسم أو الهاتف أو النص…" className={`${inputCls} !ps-10 w-64 !py-2 !text-xs`} />
        </div>
        <button onClick={exportCsv} data-testid="admin-export-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2.5 text-xs font-bold text-white hover:opacity-90"><DownloadIcon className="h-3.5 w-3.5" /> تصدير CSV</button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-brand-line bg-white" data-testid="admin-table">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-brand-line bg-brand-paper font-mono text-[10px] uppercase tracking-widest text-brand-dim">
              {['النوع', 'الاسم', 'التواصل', 'الرسالة', 'الحالة', 'التاريخ', ''].map((h, i) => <th key={i} className="px-4 py-3.5 text-start">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="px-4 py-14 text-center text-sm text-brand-dim" data-testid="admin-empty">لا توجد رسائل مطابقة.</td></tr>}
            {filtered.map((s, i) => (
              <tr key={s.id} className="border-b border-brand-line/70 text-sm hover:bg-brand-paper/70" data-testid={`admin-submission-row-${i}`}>
                <td className="px-4 py-4"><span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold ${TYPE_COLORS[s.type] || ''}`}>{TYPES[s.type] || s.type}</span></td>
                <td className="px-4 py-4 font-bold text-brand-ink">{s.name}{s.city && <span className="block text-[10px] font-normal text-brand-dim">{s.city}</span>}</td>
                <td className="px-4 py-4 text-xs text-brand-steel" dir="ltr">{s.phone && <span className="block font-mono">{s.phone}</span>}{s.email && <span className="block font-mono">{s.email}</span>}</td>
                <td className="max-w-[300px] px-4 py-4 text-xs leading-relaxed text-brand-steel">{s.message}
                  {s.meta && Object.values(s.meta).some(Boolean) && <span className="mt-1 block font-mono text-[10px] text-brand-dim" dir="ltr">{Object.entries(s.meta).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ')}</span>}
                </td>
                <td className="px-4 py-4">
                  <select value={s.status} onChange={(e) => setSt(s.id, e.target.value)} data-testid={`admin-status-select-${i}`} className={`cursor-pointer rounded-full border-0 px-3 py-1.5 font-mono text-[10px] font-bold outline-none ${STATUS_COLORS[s.status]}`}>
                    {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="px-4 py-4 font-mono text-[10px] text-brand-dim" dir="ltr">{new Date(s.created_at).toLocaleString('en-GB')}</td>
                <td className="px-4 py-4"><button onClick={() => remove(s.id)} data-testid={`admin-delete-btn-${i}`} className="grid h-8 w-8 place-items-center rounded-lg text-brand-dim hover:bg-brand-crimson/10 hover:text-brand-crimson"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
